import React, { useEffect, useState } from 'react';
import { X, Star, Heart, Gem, IndianRupee, ShoppingCart, Truck, Gift, Award, PawPrint, Tag, Check } from 'lucide-react';

const tabs = ['PRODUCT DETAILS', 'WHAT TO EXPECT', 'RETURN & POLICY'];

export default function ProductModal({ product, onClose, onAddToCart }) {
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState(product.sizes[0]);
  const [tab, setTab] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [time, setTime] = useState({ h: 3, m: 11, s: 28 });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setTime(prev => {
        let { h, m, s } = prev;
        if (s > 0) s--; else { s = 59; if (m > 0) m--; else { m = 59; if (h > 0) h--; } }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const pad = n => String(n).padStart(2, '0');

  return (
    <div className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-3xl md:max-w-4xl max-h-[100vh] sm:max-h-[92vh] overflow-y-auto rounded-none sm:rounded-2xl shadow-2xl animate-[popIn_0.25s_ease-out]">
        <button
          onClick={onClose}
          className="sticky top-3 float-right mr-3 z-20 bg-white shadow-md hover:bg-neutral-100 rounded-full p-2 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-5 sm:px-8 pt-5 sm:pt-6 pb-8">
          {/* Image */}
          <div className="rounded-xl overflow-hidden bg-neutral-100 aspect-[4/3]">
            <img src={product.gallery[activeImg]} alt={product.title} className="w-full h-full object-cover" />
          </div>

          {/* Thumbs */}
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
            {product.gallery.map((g, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${activeImg === i ? 'border-[#b8935a]' : 'border-transparent'}`}
              >
                <img src={g} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Rating */}
          <div className="mt-5 flex items-center gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            ))}
            <span className="text-sm text-neutral-700">({product.reviews}+ Reviews)</span>
          </div>

          <div className="mt-3 flex items-center gap-2 text-sm text-neutral-700">
            <Award className="w-4 h-4 text-[#b8935a]" />
            <span>RANKED <b className="text-neutral-900">NO.1 Flower BRAND BY GOOGLE</b> IN INDIA</span>
          </div>

          {/* Title */}
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl text-neutral-900 leading-tight">{product.title}</h2>

          {/* Price */}
          <div className="mt-4 flex items-end gap-3 flex-wrap">
            <span className="text-2xl sm:text-3xl font-bold text-neutral-900">Rs. {product.price.toLocaleString('en-IN')}.00</span>
            <span className="text-neutral-500 line-through text-lg">Rs. {product.originalPrice.toLocaleString('en-IN')}.00</span>
          </div>
          <div className="mt-2 inline-flex items-center gap-2 bg-[#b8935a]/20 border border-[#b8935a]/40 rounded-md px-3 py-1.5">
            <Tag className="w-4 h-4 text-[#8b6c3d]" />
            <span className="text-sm font-semibold text-[#8b6c3d]">SAVE Rs. {product.save.toLocaleString('en-IN')}</span>
          </div>

          {/* Social proof */}
          <div className="mt-5 flex items-center gap-3">
            <div className="flex -space-x-2">
              {product.gallery.slice(0, 4).map((g, i) => (
                <img key={i} src={g} alt="" className="w-9 h-9 rounded-full border-2 border-white object-cover" />
              ))}
            </div>
            <div className="bg-[#1e3a2b] text-white px-4 py-2 rounded-full text-xs sm:text-sm flex items-center gap-2">
              <span className="bg-yellow-400 text-[#1e3a2b] rounded-full w-5 h-5 grid place-items-center"><Check className="w-3 h-3" strokeWidth={3} /></span>
              <span><b>Shipped 100+ orders</b> in the last 24h</span>
            </div>
          </div>

          {/* Features */}
          <div className="mt-6 grid grid-cols-3 gap-2 text-center">
            <div className="flex flex-col items-center gap-1">
              <Heart className="w-7 h-7 text-[#1e3a2b]" />
              <span className="text-xs sm:text-sm text-neutral-800">100% Handmade</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <Gem className="w-7 h-7 text-[#1e3a2b]" />
              <span className="text-xs sm:text-sm text-neutral-800">Premium Quality</span>
            </div>
            <div className="flex flex-col items-center gap-1">
              <IndianRupee className="w-7 h-7 text-[#1e3a2b]" />
              <span className="text-xs sm:text-sm text-neutral-800">Manufacturing prices</span>
            </div>
          </div>

          {/* Stock */}
          <div className="mt-6 flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${product.stock === 'Low stock' ? 'bg-orange-500' : 'bg-green-500'}`} />
            <span className={`font-semibold ${product.stock === 'Low stock' ? 'text-orange-600' : 'text-green-700'}`}>{product.stock}</span>
          </div>

          {/* Size selector */}
          <div className="mt-5">
            <div className="text-sm text-neutral-900 font-semibold mb-2">Size - {size}</div>
            <div className="grid grid-cols-2 gap-3">
              {product.sizes.map(s => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`py-3 rounded-md text-sm font-medium border transition-all ${size === s ? 'bg-[#b8935a] text-white border-[#b8935a]' : 'bg-white text-[#b8935a] border-[#b8935a]/60 hover:border-[#b8935a]'}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <span className="text-neutral-800"><b>{10 + Math.floor(Math.random() * 20)}</b> people are viewing this product</span>
          </div>

          {/* Add to cart */}
          <button
            onClick={() => onAddToCart({ ...product, size })}
            className="mt-5 w-full bg-[#1e3a2b] hover:bg-[#2a5040] text-white font-bold text-lg py-4 rounded-lg tracking-wide transition-colors flex items-center justify-center gap-2"
          >
            <ShoppingCart className="w-5 h-5" /> ADD TO CART
          </button>

          <div className="mt-4 border-2 border-[#1e3a2b] rounded-lg py-3 px-4 text-center text-[#1e3a2b] font-semibold text-sm flex items-center justify-center gap-2">
            1 ORDER = 1 MEAL FOR FURRY FRIENDS. <PawPrint className="w-4 h-4" /> <a href="#" className="underline">Learn more..</a>
          </div>

          {/* Countdown */}
          <div className="mt-6 bg-[#1e3a2b] rounded-xl p-5 text-white text-center">
            <div className="text-xl font-bold mb-3">Hurry! Sale ends in:</div>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              {[{ v: pad(time.h), l: 'Hrs' }, { v: pad(time.m).split(''), l: 'Mins', split: true }, { v: pad(time.s).split(''), l: 'Secs', split: true }].map((box, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="text-2xl font-bold">:</span>}
                  <div>
                    <div className="flex gap-1">
                      {(Array.isArray(box.v) ? box.v : pad(box.v).split('')).map((d, k) => (
                        <div key={k} className="bg-black w-10 sm:w-12 h-12 sm:h-14 rounded-md grid place-items-center text-2xl sm:text-3xl font-bold">{d}</div>
                      ))}
                    </div>
                    <div className="text-xs mt-1">{box.l}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Timeline */}
          {/* <div className="mt-8 relative">
            <div className="absolute top-6 left-[16%] right-[16%] h-0.5 bg-[#b8935a]" />
            <div className="grid grid-cols-3 relative">
              {[
                { Icon: ShoppingCart, date: 'Aug 5th', label: 'Ordered' },
                { Icon: Truck, date: 'Aug 6th - Aug 7th', label: 'Order Ready' },
                { Icon: Gift, date: 'Aug 15th - Aug 17th', label: 'Delivered' },
              ].map((s, i) => (
                <div key={i} className="flex flex-col items-center text-center">
                  <div className="bg-[#b8935a] text-white w-12 h-12 rounded-full grid place-items-center relative z-10">
                    <s.Icon className="w-5 h-5" />
                  </div>
                  <div className="mt-2 font-bold text-sm text-neutral-900">{s.date}</div>
                  <div className="text-xs text-neutral-600">{s.label}</div>
                </div>
              ))}
            </div>
          </div> */}

          {/* Tabs */}
          <div className="mt-8">
            <div className="grid grid-cols-3 border-b border-neutral-300">
              {tabs.map((t, i) => (
                <button
                  key={t}
                  onClick={() => setTab(i)}
                  className={`px-2 py-3 text-xs sm:text-sm font-semibold text-center transition-colors ${tab === i ? 'border-b-2 border-[#1e3a2b] text-[#1e3a2b]' : 'text-neutral-500 hover:text-[#1e3a2b]'}`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="pt-5">
              {tab === 0 && (
  <div>
    <h3 className="font-serif text-xl text-neutral-900 mb-3">
      {product.title.split('|')[0]}
    </h3>

<div
  className="text-neutral-700 leading-relaxed text-sm sm:text-base"
  dangerouslySetInnerHTML={{
    __html: expanded
      ? (product.short_description || '')
      : (product.short_description || '').slice(0, 240) + '…'
  }}
/>

<div
  className="text-neutral-700 leading-relaxed text-sm sm:text-base"
  dangerouslySetInnerHTML={{
    __html: expanded
      ? (product.description || '')
      : (product.description || '').slice(0, 240) + '…'
  }}
/>

<button
  type="button"
  onClick={() => setExpanded(!expanded)}
  className="mt-2 text-neutral-900 underline text-sm font-medium"
>
  {expanded ? 'Read less' : 'Read more'}
</button>

</div>
  
)}
              {tab === 1 && (
                <ul className="list-disc pl-5 space-y-2 text-sm text-neutral-700">
                  <li>Fresh, handpicked flowers arranged by skilled florists.</li>
                  <li>Premium-quality blooms sourced from trusted growers.</li>
                  <li>Beautifully arranged and delivered fresh to your doorstep.</li>
                  <li>Same-day delivery available on selected flower orders.</li>
                </ul>
              )}
              {tab === 2 && (
                <div className="text-sm text-neutral-700 space-y-2">
                 <p>As flowers are perishable and prepared fresh for each order, we generally do not accept returns or cancellations for change of mind.</p>
<p>In case of damaged, incorrect, or poor-quality flowers, please contact us within 24 hours of delivery with clear photos. Our team will review the issue and arrange a suitable replacement or resolution.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
